// Calificación visible de un negocio de Ágora, siempre con su fuente: las reseñas verificadas de Ágora
// y el puntaje de Google son números distintos y nunca se juntan en uno (ni en la página ni en el
// JSON-LD, que solo lleva Ágora: utils/review-schema.js).
//
// - Tarjetas (home, búsqueda, categoría): UNA calificación, con la fuente en el texto. Ágora desde la
//   tercera reseña verificada (el mismo umbral que la píldora de la vidriera y el marcado); si no,
//   Google; si no hay Google, las verificadas que haya.
// - Perfil y detalle de servicio: las dos, cada una con su fuente.

import { googleMapsSameAs } from '../utils/review-schema.js';

const CARD_MIN_AGORA_REVIEWS = 3;

const validRating = (value) => {
  const rating = Number(value);
  return Number.isFinite(rating) && rating >= 1 && rating <= 5 ? rating : null;
};

/** `{ source: 'agora', rating, count }` o `null` (sin reseñas verificadas). */
export function agoraRating(rating, count) {
  const value = validRating(rating);
  const total = Number(count) || 0;
  return value != null && total > 0 ? { source: 'agora', rating: value, count: total } : null;
}

/** `{ source: 'google', rating, count, href }` de `vendor_google_place`, o `null` (oculta o sin puntaje). */
export function googleRating(place) {
  if (!place || place.is_hidden) return null;
  const value = validRating(place.rating);
  if (value == null) return null;
  return { source: 'google', rating: value, count: Number(place.review_count) || 0, href: googleMapsSameAs(place) };
}

/**
 * Las dos calificaciones del perfil. `stats` = respuesta de `GET /vendors/:u/reviews` (`rating`,
 * `review_count`); sin ella se usa `vendor.review_stats`. Google respeta «mostrar reseñas de Google».
 */
export function profileRatings(vendor, stats) {
  const reviewStats = vendor?.review_stats || {};
  const agora = stats
    ? agoraRating(stats.rating, stats.review_count)
    : reviewStats.show_agora_reviews === false
      ? null
      : agoraRating(reviewStats.agora_rating, reviewStats.agora_review_count);
  const google = reviewStats.show_google_reviews === false ? null : googleRating(vendor?.vendor_google_place);
  return { agora, google };
}

/** La calificación que muestra una tarjeta (ver arriba), o `null`. */
export function cardRating({ agora, google }) {
  if (agora && agora.count >= CARD_MIN_AGORA_REVIEWS) return agora;
  return google || agora || null;
}

/** «4.9», con punto como el resto del marketplace. */
export const formatRating = (rating) => Number(rating).toFixed(1);

const count = (n) => Number(n).toLocaleString('es-AR');

/** Texto corto de tarjeta: «26 en Google», «6 verificadas», «1 verificada». */
export function cardRatingLabel({ source, count: total }) {
  if (source === 'agora') return `${count(total)} ${total === 1 ? 'verificada' : 'verificadas'}`;
  return total > 0 ? `${count(total)} en Google` : 'en Google';
}

/** Texto del perfil: «80 reseñas en Google», «6 reseñas verificadas en Ágora». */
export function profileRatingLabel({ source, count: total }) {
  if (source === 'agora') return `${count(total)} ${total === 1 ? 'reseña verificada' : 'reseñas verificadas'} en Ágora`;
  return total > 0 ? `${count(total)} ${total === 1 ? 'reseña' : 'reseñas'} en Google` : 'en Google';
}
